import { createContext, useContext, useEffect } from "react";
import { resolveSeo, type ResolvedSeo, type SeoProps } from "@/lib/seo";

/**
 * Document-head manager.
 *
 * In the browser, tags are applied directly to <head> from an effect:
 * react-helmet-async v3 did not render tags in this Lovable/Vite build, so
 * existing tags from index.html are updated in place and page-specific JSON-LD
 * is tagged and cleaned up on route change to avoid leaking.
 *
 * During the build-time prerender, the page's metadata is handed to the
 * collector supplied through `SeoCollector`, and written into that page's
 * static HTML (see build/compose.ts) so crawlers see it without running JS.
 */

export const SeoCollector = createContext<((seo: ResolvedSeo) => void) | null>(null);

const LD_FLAG = "data-seo-jsonld";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const Seo = (props: SeoProps) => {
  const collect = useContext(SeoCollector);
  const { title, description, url, image, ogType, jsonLd, noindex } = resolveSeo(props);
  if (collect) collect(resolveSeo(props));

  useEffect(() => {
    document.title = title;

    upsertMeta("name", "description", description);
    upsertLink("canonical", url);

    // Only emit a robots directive when a page opts out of indexing (e.g. 404);
    // remove any stale one left by a previous route so normal pages stay indexable.
    document.head.querySelector('meta[name="robots"][data-seo]')?.remove();
    if (noindex) {
      const m = document.createElement("meta");
      m.setAttribute("name", "robots");
      m.setAttribute("content", "noindex, follow");
      m.setAttribute("data-seo", "");
      document.head.appendChild(m);
    }

    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);

    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // Page-specific structured data — replace any from the previous route
    // (including the copy the prerender wrote into the static HTML).
    document.head.querySelectorAll(`script[${LD_FLAG}]`).forEach((n) => n.remove());
    jsonLd.forEach((ld) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(LD_FLAG, "");
      s.text = JSON.stringify(ld);
      document.head.appendChild(s);
    });

    return () => {
      document.head.querySelectorAll(`script[${LD_FLAG}]`).forEach((n) => n.remove());
    };
    // jsonLd is rebuilt each render; its serialised form is the stable dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, url, image, ogType, JSON.stringify(jsonLd), noindex]);

  return null;
};

export default Seo;
